const POPUP_POLL_INTERVAL_MS = 500;
const POPUP_CLOSED_GRACE_MS = 2_000;

/**
 * Observes popup closure without relying on a provider callback. A short grace
 * period lets queued success callbacks settle before treating closure as cancel.
 *
 * @param popup - Window reference returned by the browser when opening the popup.
 * @param onClosed - Called once after the popup has remained closed for the grace period.
 * @returns Cleanup to stop polling and remove opener lifecycle listeners.
 */
export const observePopupClosed = (
  popup: Pick<Window, 'closed'>,
  onClosed: () => void
): (() => void) => {
  let closedAt: number | null = null;
  let stopped = false;

  const checkClosed = () => {
    if (stopped) return;
    try {
      if (!popup.closed) {
        closedAt = null;
        return;
      }
    } catch {
      // A restricted WindowProxy is not evidence of a user cancellation.
      closedAt = null;
      return;
    }
    closedAt ??= Date.now();
    if (Date.now() - closedAt < POPUP_CLOSED_GRACE_MS) return;
    stop();
    onClosed();
  };

  const interval = window.setInterval(checkClosed, POPUP_POLL_INTERVAL_MS);
  const stop = () => {
    stopped = true;
    window.clearInterval(interval);
    window.removeEventListener('focus', checkClosed);
    document.removeEventListener('visibilitychange', checkClosed);
  };
  window.addEventListener('focus', checkClosed);
  document.addEventListener('visibilitychange', checkClosed);
  return stop;
};
